"use client";

import { useState, useRef } from "react";
import { useAggregateCommand } from "@/hooks/commands";
import { FileText, Download, FileUp, Loader2, AlertCircle, CheckCircle } from "lucide-react";
import { createClient } from "@/lib/supabase/client";
import {
  DealDocumentStore,
  DealDocumentStorageError,
  ownerDealDocumentPath,
} from "@/lib/storage/deal-documents.store";
import { timestampedFileName } from "@/lib/storage/paths";
import type { DealDocumentWithHref } from "@/lib/deals/types";

const ALLOWED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "text/plain",
  "text/markdown",
  "text/csv",
  "application/rtf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const ALLOWED_EXTENSIONS = "PDF, JPG, PNG, WEBP, TXT, CSV, RTF, DOC, DOCX";
/** The same set as storage-path extensions (lowercase, no dot). */
const ALLOWED_EXTENSIONS_FOR_PATH = [
  "pdf",
  "jpg",
  "jpeg",
  "png",
  "webp",
  "txt",
  "md",
  "csv",
  "rtf",
  "doc",
  "docx",
];
const MAX_SIZE_MB = 20;

interface DealDocumentsSectionProps {
  dealId: string;
  documents: DealDocumentWithHref[];
  userId: string;
  onDocumentUploaded?: () => void;
  compact?: boolean;
}

/**
 * DealDocumentsSection - Combined document list + upload for deals
 *
 * Shows existing role-filtered documents with download links and provides
 * an upload area for adding new documents to the deal.
 */
export function DealDocumentsSection({
  dealId,
  documents,
  userId,
  onDocumentUploaded,
  compact = false,
}: DealDocumentsSectionProps) {
  const supabase = createClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isUploading, setIsUploading] = useState(false);
  // One operation per document: a retry after a lost answer files it once.
  const { send: fileDocument } = useAggregateCommand<
    { name: string; path: string; type: string | null },
    unknown
  >({ method: "POST" });
  const [error, setError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    setUploadSuccess(false);

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(`Nur erlaubt: ${ALLOWED_EXTENSIONS}`);
      return;
    }

    // Validate file size
    const maxBytes = MAX_SIZE_MB * 1024 * 1024;
    if (file.size > maxBytes) {
      setError(`Datei zu groß. Maximum: ${MAX_SIZE_MB}MB`);
      return;
    }

    setIsUploading(true);

    try {
      // `{user_id}/deal-{id}/{timestamp}.{ext}` — owner-scoped by construction,
      // which is what the bucket's INSERT policy requires. This upload runs in
      // the BROWSER with the user's own session, so RLS is the enforcement
      // (`.claude/rules/storage.md` §Browser uploads).
      const filePath = ownerDealDocumentPath(
        userId,
        `deal-${dealId}`,
        timestampedFileName(file.name, ALLOWED_EXTENSIONS_FOR_PATH, "pdf")
      );

      await DealDocumentStore.upload(supabase, filePath, file, {
        contentType: file.type,
        upsert: true,
      });

      // Persist the storage PATH; downloads mint short-lived URLs on demand via
      // the document-url seam (finding H3).
      const outcome = await fileDocument({
        url: `/api/deals/${dealId}/documents`,
        input: { name: file.name, path: filePath, type: file.type || null },
        revision: null,
      });
      if (outcome.kind !== "committed") throw new Error(outcome.message);

      setUploadSuccess(true);
      setTimeout(() => setUploadSuccess(false), 3000);
      onDocumentUploaded?.();
    } catch (err) {
      // A storage failure carries the raw Supabase message (English, and it
      // names the bucket and the policy), so it is reported as the fixed German
      // string the deleted inline `throw new Error("Fehler beim Hochladen")`
      // used to produce. Only OUR errors are safe to show verbatim.
      setError(
        err instanceof DealDocumentStorageError
          ? "Fehler beim Hochladen"
          : err instanceof Error
            ? err.message
            : "Fehler beim Hochladen"
      );
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <h3 className="font-semibold text-zinc-900 mb-3 flex items-center gap-2 text-sm">
        <FileText className="w-4 h-4 text-zinc-400" />
        Dokumente
        {documents.length > 0 && (
          <span className="text-zinc-400 font-normal">({documents.length})</span>
        )}
      </h3>

      {/* Existing documents list */}
      {documents.length > 0 && (
        <ul className={`space-y-2 ${compact ? "mb-3" : "mb-4"}`}>
          {documents.map((doc, index) => (
            <li key={index} className="flex items-center justify-between p-2 bg-zinc-50 rounded-lg">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-medium text-zinc-900 truncate">{doc.name}</p>
                  <div className="flex items-center gap-1.5">
                    {doc.uploaded_at && (
                      <span className="text-[10px] text-zinc-500">
                        {new Date(doc.uploaded_at).toLocaleDateString("de-DE")}
                      </span>
                    )}
                    {doc.uploaded_by && (
                      <span
                        className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                          doc.uploaded_by === "seller"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-blue-100 text-blue-700"
                        }`}
                      >
                        {doc.uploaded_by === "seller" ? "Verkäufer" : "Käufer"}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <a
                href={doc.downloadHref}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-primary hover:bg-primary/10 rounded transition-colors flex-shrink-0"
                title="Herunterladen"
              >
                <Download className="w-3 h-3" />
              </a>
            </li>
          ))}
        </ul>
      )}

      {/* Upload area */}
      <button
        type="button"
        onClick={handleUploadClick}
        disabled={isUploading}
        className="w-full p-3 border-2 border-dashed border-zinc-300 rounded-lg text-center hover:border-zinc-400 hover:bg-zinc-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isUploading ? (
          <div className="flex flex-col items-center gap-1.5">
            <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
            <span className="text-xs text-zinc-500">Wird hochgeladen...</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1.5">
            <FileUp className="w-5 h-5 text-zinc-400" />
            <span className="text-xs text-zinc-600">Dokument hochladen</span>
            <span className="text-[10px] text-zinc-400">Max. {MAX_SIZE_MB}MB</span>
          </div>
        )}
      </button>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_TYPES.join(",")}
        onChange={handleFileChange}
        className="hidden"
        disabled={isUploading}
      />

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 mt-2 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Success */}
      {uploadSuccess && (
        <div className="flex items-center gap-2 mt-2 p-2 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700">
          <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
          Dokument hochgeladen
        </div>
      )}
    </div>
  );
}
